import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import {
  canProject,
  PRD_SOURCE_MAX_FILES,
  type DocumentPointer,
  type DocumentVersion,
  type Job,
  type PrdContent,
  type PrdGoal,
  type StoryPriority,
  type UserStory,
} from '@allemnii-pulse/shared';
import { useDocument } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { MarkdownEditor } from '../../components/ui/MarkdownEditor.js';
import { Label } from '../../components/ui/Label.js';
import { Term } from '../../components/ui/Term.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import { ExportMenu } from '../exports/ExportMenu.js';
import { PrdBrainstormDialog } from './PrdBrainstormDialog.js';
import { SOURCE_FILE_ACCEPT, uploadErrorMessage, uploadSourceFile } from '../../lib/sourceUploads.js';

type DraftPrd = Omit<PrdContent, 'userStories'> & {
  userStories: Array<Omit<UserStory, 'id'> & { id?: string }>;
};

const EMPTY_DRAFT: DraftPrd = {
  projectName: '',
  startDate: new Date().toISOString().slice(0, 10),
  endDate: null,
  projectLeadUid: '',
  problemStatement: '',
  targetAudience: [''],
  userStories: [{ role: '', want: '', benefit: '' }],
  outOfScope: [],
};

const NO_PRIORITY = '__none__';
const STORY_PRIORITY_LABEL: Record<StoryPriority, string> = {
  must: 'Must have',
  should: 'Should have',
  could: 'Could have',
};

/** Blank rows are what the "+ Add" buttons create, so they're dropped on save rather than
 * rejected by the API. An optional section left empty is omitted, meaning "not stated". */
function cleanList(items: string[] | undefined): string[] | undefined {
  const kept = (items ?? []).map((i) => i.trim()).filter(Boolean);
  return kept.length > 0 ? kept : undefined;
}

function cleanGoals(goals: PrdGoal[] | undefined): PrdGoal[] | undefined {
  const kept = (goals ?? [])
    .filter((g) => g.goal.trim())
    .map((g) => ({
      goal: g.goal.trim(),
      ...(g.metric?.trim() ? { metric: g.metric.trim() } : {}),
      ...(g.target?.trim() ? { target: g.target.trim() } : {}),
    }));
  return kept.length > 0 ? kept : undefined;
}

/** Merges an extracted or brainstormed PRD into the form. Only fields the model actually filled
 * in replace what's there, so a blank or missing value never wipes the default start date or
 * anything the user already typed. */
function mergeGenerated(draft: DraftPrd, generated: Partial<DraftPrd>): DraftPrd {
  const next = { ...draft } as Record<string, unknown>;
  for (const [key, value] of Object.entries(generated)) {
    const blank =
      value == null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0);
    if (!blank) next[key] = value;
  }
  return next as DraftPrd;
}

/** A repeatable single-line list — target audience, out of scope, risks and the like. */
function StringListField({
  label,
  items,
  onChange,
  addLabel,
  removeLabel,
  placeholder,
  compact = false,
}: {
  label: ReactNode;
  items: string[] | undefined;
  onChange: (items: string[]) => void;
  addLabel: string;
  removeLabel: string;
  placeholder?: string;
  compact?: boolean;
}) {
  const list = items ?? [];
  return (
    <div className={`flex flex-col gap-1.5 ${compact ? 'text-xs' : 'text-sm'}`}>
      <span className={compact ? 'text-muted' : undefined}>{label}</span>
      {list.map((value, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(list.map((x, xi) => (xi === i ? e.target.value : x)))}
            className={compact ? 'h-8 flex-1 bg-surface text-xs' : 'flex-1'}
          />
          <button
            type="button"
            aria-label={removeLabel}
            onClick={() => onChange(list.filter((_, xi) => xi !== i))}
            className={`flex flex-shrink-0 items-center justify-center rounded-lg text-muted hover:text-danger ${compact ? 'h-8 w-8' : 'h-9 w-9'}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, ''])}
        className="self-start text-xs text-muted hover:text-accent"
      >
        + {addLabel}
      </button>
    </div>
  );
}

export function PrdPage() {
  const { projectId, project, role } = useProject();
  const canAccept = canProject(role, 'document.accept');
  const { data: pointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/prd`);
  const { data: currentVersion } = useDocument<DocumentVersion>(
    pointer?.currentVersionId
      ? `projects/${projectId}/documents/prd/versions/${pointer.currentVersionId}`
      : null,
  );

  const [draft, setDraft] = useState<DraftPrd>(EMPTY_DRAFT);
  const [savedVersionId, setSavedVersionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractJobId, setExtractJobId] = useState<string | null>(null);
  const [extractedFileCount, setExtractedFileCount] = useState(0);
  const [brainstormOpen, setBrainstormOpen] = useState(false);
  const [brainstormApplied, setBrainstormApplied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedFromVersion = useRef<string | null>(null);

  // Seed the form from the current (possibly accepted) version once it loads — but only once
  // per version, so the user's in-progress edits aren't clobbered by the live listener.
  useEffect(() => {
    if (currentVersion && loadedFromVersion.current !== currentVersion.id) {
      loadedFromVersion.current = currentVersion.id;
      setDraft(currentVersion.content as DraftPrd);
    }
  }, [currentVersion]);

  // No PRD draft/version exists yet — carry the project's name over from creation instead of
  // leaving the field blank.
  useEffect(() => {
    if (!currentVersion && !pointer?.currentVersionId && project && !loadedFromVersion.current) {
      setDraft((d) => (d.projectName ? d : { ...d, projectName: project.name }));
    }
  }, [project, currentVersion, pointer]);

  const { data: extractJob } = useDocument<Job>(extractJobId ? `jobs/${extractJobId}` : null);
  useEffect(() => {
    if (extractJob?.status === 'succeeded' && extractJob.result) {
      const extracted = extractJob.result as Partial<DraftPrd>;
      setDraft((d) => mergeGenerated(d, extracted));
      setExtractJobId(null);
    } else if (extractJob?.status === 'failed') {
      setError(extractJob.error ?? 'Extraction failed');
      setExtractJobId(null);
    }
  }, [extractJob]);

  /** Uploads every chosen file, then extracts one draft from all of them together. */
  async function handleUpload(files: File[]) {
    setError(null);
    if (files.length > PRD_SOURCE_MAX_FILES) {
      setError(`Upload up to ${PRD_SOURCE_MAX_FILES} documents at a time.`);
      return;
    }
    setBusy(true);
    try {
      const storagePaths = await Promise.all(files.map((f) => uploadSourceFile(projectId, 'prd', f)));
      const { jobId } = await api.post<{ jobId: string }>(`/projects/${projectId}/prd/extract`, {
        storagePaths,
      });
      setExtractedFileCount(files.length);
      setExtractJobId(jobId);
    } catch (err) {
      setError(uploadErrorMessage(err, 'Upload or extraction failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveDraft() {
    setBusy(true);
    setError(null);
    try {
      const { userStories, ...rest } = draft;
      const result = await api.put<{ versionId: string }>(`/projects/${projectId}/prd`, {
        ...rest,
        projectLeadUid: project?.leadUid ?? draft.projectLeadUid,
        // Keep each existing story's id so tasks traced to it stay linked; new stories have none
        // and get one assigned by the server.
        userStories: userStories.map(({ id, role, want, benefit, acceptanceCriteria, priority }) => ({
          id,
          role,
          want,
          benefit,
          acceptanceCriteria: cleanList(acceptanceCriteria),
          priority,
        })),
        goals: cleanGoals(draft.goals),
        nonFunctionalRequirements: cleanList(draft.nonFunctionalRequirements),
        assumptions: cleanList(draft.assumptions),
        dependencies: cleanList(draft.dependencies),
        risks: cleanList(draft.risks),
        openQuestions: cleanList(draft.openQuestions),
      });
      setSavedVersionId(result.versionId);
      setBrainstormApplied(false);
    } catch {
      setError('Could not save the draft.');
    } finally {
      setBusy(false);
    }
  }

  async function handleAccept() {
    const versionId = savedVersionId ?? pointer?.currentVersionId;
    if (!versionId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/prd/accept`, { versionId });
      setSavedVersionId(null);
    } catch {
      setError('Could not accept this version.');
    } finally {
      setBusy(false);
    }
  }

  const isAccepted = pointer?.status === 'accepted' && !savedVersionId;
  const formHasContent =
    draft.problemStatement.trim() !== '' || draft.userStories.some((s) => s.want.trim() !== '');

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      {/* Sticky only from md: on a phone the wrapped toolbar is several rows tall and would eat
          most of the screen once the on-screen keyboard is up. */}
      <div className="flex flex-col gap-3 border-b border-border bg-bg py-3 sm:flex-row sm:flex-wrap sm:items-center md:sticky md:top-0 md:z-10">
        <div>
          <h1 className="text-lg font-semibold">
            <Term id="prd" />
          </h1>
          <p className="text-sm text-muted">
            {pointer?.status === 'accepted'
              ? `Accepted — version ${pointer.currentVersionNumber}`
              : pointer?.status === 'draft'
                ? 'Draft — not yet accepted'
                : 'Not started'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:justify-end">
          {pointer?.currentVersionId && <ExportMenu projectId={projectId} docType="prd" />}
          {pointer && pointer.currentVersionNumber > 1 && (
            <Link to={`/projects/${projectId}/documents/prd/history`}>
              <Button type="button">History</Button>
            </Link>
          )}
          <Button type="button" disabled={busy} onClick={() => setBrainstormOpen(true)}>
            Brainstorm
          </Button>
          <label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={SOURCE_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                // Cleared so picking the same files again still fires onChange.
                e.target.value = '';
                if (files.length > 0) void handleUpload(files);
              }}
            />
            <Button
              type="button"
              disabled={busy}
              title={`Up to ${PRD_SOURCE_MAX_FILES} documents, read together into one draft`}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload documents
            </Button>
          </label>
          <Button disabled={busy} onClick={() => void handleSaveDraft()}>
            Save draft
          </Button>
          {canAccept ? (
            <Button
              variant="default"
              disabled={busy || isAccepted || (!savedVersionId && !pointer?.currentVersionId)}
              onClick={() => void handleAccept()}
            >
              Accept &amp; create version
            </Button>
          ) : (
            <span className="text-xs text-muted">A project lead or owner accepts versions.</span>
          )}
        </div>
      </div>

      <PrdBrainstormDialog
        projectId={projectId}
        open={brainstormOpen}
        onOpenChange={setBrainstormOpen}
        formHasContent={formHasContent}
        onDraft={(prd) => {
          setDraft((d) => mergeGenerated(d, prd as Partial<DraftPrd>));
          setBrainstormApplied(true);
        }}
      />

      {/* The model may decide it has enough after any round, so the dialog can close sooner than
          expected — say what happened rather than leave the user guessing. */}
      {brainstormApplied && (
        <Card className="flex items-center gap-3 p-4 text-sm">
          <span className="flex-1">
            The brainstorm drafted the PRD below. Review it, then save the draft — nothing is saved yet.
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setBrainstormApplied(false)}
            className="-m-2 p-2 text-muted hover:text-text"
          >
            <X className="h-4 w-4" />
          </button>
        </Card>
      )}
      {extractJobId && (
        <Card className="p-4 text-sm text-muted">
          {extractedFileCount > 1
            ? `Reading the ${extractedFileCount} uploaded documents…`
            : 'Reading the uploaded document…'}
        </Card>
      )}
      {error && <Card className="border-danger p-4 text-sm text-danger">{error}</Card>}

      <Card className="flex flex-col gap-5 p-4 sm:p-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="prd-project-name">Project name</Label>
          <Input
            id="prd-project-name"
            value={draft.projectName}
            onChange={(e) => setDraft((d) => ({ ...d, projectName: e.target.value }))}
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex min-w-[10rem] flex-1 flex-col gap-1.5">
            <Label htmlFor="prd-start-date">Start date</Label>
            <DatePicker
              id="prd-start-date"
              value={draft.startDate}
              onChange={(v) => setDraft((d) => ({ ...d, startDate: v }))}
            />
          </div>
          <div className="flex min-w-[10rem] flex-1 flex-col gap-1.5">
            <Label htmlFor="prd-end-date">End date (optional)</Label>
            <DatePicker
              id="prd-end-date"
              value={draft.endDate}
              onChange={(v) => setDraft((d) => ({ ...d, endDate: v }))}
              onClear={() => setDraft((d) => ({ ...d, endDate: null }))}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="prd-problem-statement">Problem statement</Label>
          <MarkdownEditor
            id="prd-problem-statement"
            rows={4}
            value={draft.problemStatement}
            onChange={(value) => setDraft((d) => ({ ...d, problemStatement: value }))}
          />
        </div>

        <div className="flex flex-col gap-1.5 text-sm">
          <span>Goals &amp; success metrics (optional)</span>
          {(draft.goals ?? []).map((g, i) => {
            const setGoal = (patch: Partial<PrdGoal>) =>
              setDraft((d) => ({
                ...d,
                goals: (d.goals ?? []).map((x, xi) => (xi === i ? { ...x, ...patch } : x)),
              }));
            return (
              <div key={i} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  placeholder="goal"
                  value={g.goal}
                  onChange={(e) => setGoal({ goal: e.target.value })}
                  className="sm:flex-[2]"
                />
                <Input
                  placeholder="how it's measured"
                  value={g.metric ?? ''}
                  onChange={(e) => setGoal({ metric: e.target.value })}
                  className="sm:flex-[2]"
                />
                <Input
                  placeholder="target"
                  value={g.target ?? ''}
                  onChange={(e) => setGoal({ target: e.target.value })}
                  className="sm:flex-1"
                />
                <button
                  type="button"
                  aria-label="Remove goal"
                  onClick={() =>
                    setDraft((d) => ({ ...d, goals: (d.goals ?? []).filter((_, xi) => xi !== i) }))
                  }
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center self-end rounded-lg text-muted hover:text-danger sm:self-auto"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setDraft((d) => ({ ...d, goals: [...(d.goals ?? []), { goal: '' }] }))}
            className="self-start text-xs text-muted hover:text-accent"
          >
            + Add goal
          </button>
        </div>

        <StringListField
          label="Target audience"
          items={draft.targetAudience}
          onChange={(targetAudience) => setDraft((d) => ({ ...d, targetAudience }))}
          addLabel="Add audience"
          removeLabel="Remove audience"
        />

        <div className="flex flex-col gap-2 text-sm">
          <span>Key user stories</span>
          {draft.userStories.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col gap-2 rounded-lg border border-border bg-bg p-3"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <Input
                  placeholder="role"
                  value={s.role}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      userStories: d.userStories.map((x, xi) =>
                        xi === i ? { ...x, role: e.target.value } : x,
                      ),
                    }))
                  }
                  className="h-8 bg-surface text-xs sm:flex-1"
                />
                <Input
                  placeholder="want"
                  value={s.want}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      userStories: d.userStories.map((x, xi) =>
                        xi === i ? { ...x, want: e.target.value } : x,
                      ),
                    }))
                  }
                  className="h-8 bg-surface text-xs sm:flex-[2]"
                />
                <Input
                  placeholder="so that / benefit"
                  value={s.benefit}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      userStories: d.userStories.map((x, xi) =>
                        xi === i ? { ...x, benefit: e.target.value } : x,
                      ),
                    }))
                  }
                  className="h-8 bg-surface text-xs sm:flex-[2]"
                />
                <button
                  type="button"
                  aria-label="Remove user story"
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      userStories: d.userStories.filter((_, xi) => xi !== i),
                    }))
                  }
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center self-end rounded-lg text-muted hover:text-danger sm:self-auto"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <Select
                  value={s.priority ?? NO_PRIORITY}
                  onValueChange={(v) =>
                    setDraft((d) => ({
                      ...d,
                      userStories: d.userStories.map((x, xi) =>
                        xi === i
                          ? { ...x, priority: v === NO_PRIORITY ? undefined : (v as StoryPriority) }
                          : x,
                      ),
                    }))
                  }
                >
                  <SelectTrigger aria-label="Story priority" className="h-8 bg-surface text-xs sm:w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PRIORITY}>No priority</SelectItem>
                    {(Object.keys(STORY_PRIORITY_LABEL) as StoryPriority[]).map((p) => (
                      <SelectItem key={p} value={p}>
                        {STORY_PRIORITY_LABEL[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="min-w-0 flex-1">
                  <StringListField
                    compact
                    label="Acceptance criteria"
                    items={s.acceptanceCriteria}
                    onChange={(acceptanceCriteria) =>
                      setDraft((d) => ({
                        ...d,
                        userStories: d.userStories.map((x, xi) =>
                          xi === i ? { ...x, acceptanceCriteria } : x,
                        ),
                      }))
                    }
                    addLabel="Add criterion"
                    removeLabel="Remove criterion"
                  />
                </div>
              </div>
            </motion.div>
          ))}
          <button
            type="button"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                userStories: [...d.userStories, { role: '', want: '', benefit: '' }],
              }))
            }
            className="self-start text-xs text-muted hover:text-accent"
          >
            + Add story
          </button>
        </div>

        <StringListField
          label="Non-functional requirements & constraints (optional)"
          placeholder="e.g. Pages load in under 2s on 4G"
          items={draft.nonFunctionalRequirements}
          onChange={(nonFunctionalRequirements) =>
            setDraft((d) => ({ ...d, nonFunctionalRequirements }))
          }
          addLabel="Add requirement"
          removeLabel="Remove requirement"
        />

        <StringListField
          label={<Term id="out_of_scope" />}
          items={draft.outOfScope}
          onChange={(outOfScope) => setDraft((d) => ({ ...d, outOfScope }))}
          addLabel="Add item"
          removeLabel="Remove item"
        />

        <StringListField
          label="Assumptions (optional)"
          items={draft.assumptions}
          onChange={(assumptions) => setDraft((d) => ({ ...d, assumptions }))}
          addLabel="Add assumption"
          removeLabel="Remove assumption"
        />

        <StringListField
          label="Dependencies (optional)"
          placeholder="e.g. Payments team's refund API"
          items={draft.dependencies}
          onChange={(dependencies) => setDraft((d) => ({ ...d, dependencies }))}
          addLabel="Add dependency"
          removeLabel="Remove dependency"
        />

        <StringListField
          label="Risks (optional)"
          items={draft.risks}
          onChange={(risks) => setDraft((d) => ({ ...d, risks }))}
          addLabel="Add risk"
          removeLabel="Remove risk"
        />

        <StringListField
          label="Open questions (optional)"
          items={draft.openQuestions}
          onChange={(openQuestions) => setDraft((d) => ({ ...d, openQuestions }))}
          addLabel="Add question"
          removeLabel="Remove question"
        />
      </Card>

      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 p-3 text-xs text-muted">
        Nothing is written to the project until you save. Accepting records who validated this
        version — see <Term id="audit_log">audit log</Term>.
      </div>
    </div>
  );
}
